import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { ClaimsMap } from '@/components/ClaimsMap';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from 'recharts';
import { DollarSign, FileText, TrendingUp, AlertCircle, Users, Target, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#2563EB', '#059669', '#EA580C', '#EF4444', '#8B5CF6', '#10B981'];

// Chart styling constants (extracted to avoid re-renders)
const CHART_TICK_STYLE = { fontSize: 12 };
const CHART_LABEL_STYLE = { fontWeight: 'bold' };

export const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [claimsByStatus, setClaimsByStatus] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [monthlySettlement, setMonthlySettlement] = useState([]);
  const [revenueForecast, setRevenueForecast] = useState(null);
  const [claimsByAdjuster, setClaimsByAdjuster] = useState([]);
  const [allClaims, setAllClaims] = useState([]);
  const [mapExpanded, setMapExpanded] = useState(false);
  const [selectedFirm, setSelectedFirm] = useState('all');
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      
      const firmParam = selectedFirm !== 'all' ? `?public_adjusting_firm=${selectedFirm}` : '';
      
      const [statsRes, statusRes, activityRes, monthlyRes, forecastRes, adjusterRes, claimsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats${firmParam}`),
        axios.get(`${API}/dashboard/claims-by-status`),
        axios.get(`${API}/dashboard/recent-activity?limit=10`),
        axios.get(`${API}/dashboard/monthly-settlement`),
        axios.get(`${API}/dashboard/revenue-forecast`),
        axios.get(`${API}/dashboard/claims-by-adjuster`),
        axios.get(`${API}/claims`)
      ]);

      setStats(statsRes.data);
      setClaimsByStatus(statusRes.data.data);
      setRecentActivity(activityRes.data.activities);
      setMonthlySettlement(monthlyRes.data.data);
      setRevenueForecast(forecastRes.data);
      setClaimsByAdjuster(adjusterRes.data.data);
      // Claims API returns array directly
      setAllClaims(Array.isArray(claimsRes.data) ? claimsRes.data : []);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [selectedFirm]);

  useEffect(() => {
    fetchDashboardData();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchDashboardData();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  const kpiCards = stats ? [
    {
      title: 'Total Claims',
      value: stats.total_claims,
      icon: FileText,
      color: 'text-accent',
      bgColor: 'bg-accent/10'
    },
    {
      title: 'Open Claims',
      value: stats.open_claims,
      icon: AlertCircle,
      color: 'text-warning-text',
      bgColor: 'bg-warning-bg'
    },
    {
      title: 'Actual Settlement',
      value: `$${stats.total_actual_settlement.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      icon: Target,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      highlight: true,
      highlightColor: 'amber'
    },
    {
      title: 'Actual Fee Outstanding',
      value: `$${stats.actual_fees_outstanding.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      icon: DollarSign,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      highlight: true,
      highlightColor: 'emerald'
    },
    {
      title: 'Total Revenue',
      value: `$${stats.total_revenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      icon: TrendingUp,
      color: 'text-success-text',
      bgColor: 'bg-success-bg'
    }
  ] : [];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-screen" data-testid="loading-spinner">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
        </div>
      </DashboardLayout>
    );
  }

  // Helper function for KPI card border styling
  const getCardBorderClass = (card) => {
    if (!card.highlight) return 'border-border';
    return card.highlightColor === 'emerald' 
      ? 'border-emerald-300 ring-2 ring-emerald-200' 
      : 'border-amber-300 ring-2 ring-amber-200';
  };

  // Helper function for KPI card text styling
  const getCardTextClass = (card) => {
    if (!card.highlight) return 'text-foreground';
    return card.highlightColor === 'emerald' ? 'text-emerald-700' : 'text-amber-700';
  };

  return (
    <DashboardLayout>
      <div className="p-4 md:p-8" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 md:mb-8 gap-4">
          <div>
            <h1 className="text-2xl md:text-4xl font-bold font-sans text-foreground tracking-tight">Dashboard</h1>
            <p className="text-sm md:text-base text-foreground-muted mt-1 md:mt-2">Overview of claims and financial metrics</p>
          </div>
          <div className="flex items-center gap-4">
            <Select value={selectedFirm} onValueChange={setSelectedFirm}>
              <SelectTrigger className="w-full md:w-[200px]" data-testid="firm-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Firms</SelectItem>
                <SelectItem value="Apicella">Apicella</SelectItem>
                <SelectItem value="MHA">MHA</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-6 mb-6 md:mb-8">
          {kpiCards.map((card) => {
            const Icon = card.icon;
            return (
              <Card 
                key={card.title} 
                className={`p-4 md:p-6 border shadow-sm ${getCardBorderClass(card)}`}
                data-testid={`kpi-${card.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs md:text-sm text-foreground-muted font-medium mb-1 md:mb-2 truncate">{card.title}</p>
                    <p className={`text-lg md:text-3xl font-bold font-mono truncate ${getCardTextClass(card)}`}>{card.value}</p>
                  </div>
                  <div className={`${card.bgColor} ${card.color} p-2 md:p-3 rounded-lg ml-2 flex-shrink-0`}>
                    <Icon className="w-4 h-4 md:w-6 md:h-6" />
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Claims Map Widget */}
        {allClaims.length > 0 && (
          <Card className="p-4 md:p-6 border border-border shadow-sm mb-6 md:mb-8" data-testid="claims-map-widget">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-accent" />
                <h3 className="text-lg md:text-xl font-semibold font-sans text-foreground">Claims Map</h3>
              </div>
              <span className="text-sm text-foreground-muted">{allClaims.length} claims</span>
            </div>
            <ClaimsMap 
              claims={allClaims} 
              expanded={mapExpanded}
              onToggleExpand={() => setMapExpanded(!mapExpanded)}
            />
          </Card>
        )}

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
          {/* Claims by Status */}
          <Card className="p-4 md:p-6 border border-border shadow-sm" data-testid="claims-by-status-chart">
            <h3 className="text-lg md:text-xl font-semibold font-sans text-foreground mb-4 md:mb-6">Claims by Status</h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={claimsByStatus}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry) => entry.status}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {claimsByStatus.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>

          {/* Financial Overview */}
          <Card className="p-6 border border-border shadow-sm" data-testid="financial-overview">
            <h3 className="text-xl font-semibold font-sans text-foreground mb-6">Financial Overview</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center py-3 border-b border-border">
                <span className="text-foreground-muted">Expected Settlement Value</span>
                <span className="text-xl font-bold font-mono text-foreground">
                  ${stats?.expected_settlement_value.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between items-center py-3 border-b-2 border-amber-300 bg-amber-50 -mx-6 px-6">
                <span className="text-amber-700 font-medium">Actual Settlement Value</span>
                <span className="text-xl font-bold font-mono text-amber-700">
                  ${stats?.total_actual_settlement.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-border">
                <span className="text-foreground-muted">Total Expected Fees</span>
                <span className="text-xl font-bold font-mono text-foreground">
                  ${stats?.total_expected_fees.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between items-center py-3 border-b-2 border-emerald-300 bg-emerald-50 -mx-6 px-6">
                <span className="text-emerald-700 font-medium">Actual Fee Outstanding</span>
                <span className="text-xl font-bold font-mono text-emerald-700">
                  ${stats?.actual_fees_outstanding.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-border">
                <span className="text-foreground-muted">Fees Collected</span>
                <span className="text-xl font-bold font-mono text-success-text">
                  ${stats?.total_fees_collected.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between items-center py-3">
                <span className="text-foreground-muted">Average Claim Age</span>
                <span className="text-xl font-bold font-mono text-foreground">
                  {Math.round(stats?.claims_aging_avg || 0)} days
                </span>
              </div>
            </div>
          </Card>
        </div>

        {/* Revenue Forecast Card */}
        {revenueForecast && (
          <Card className="p-4 md:p-6 border border-border shadow-sm mb-6 md:mb-8" data-testid="revenue-forecast">
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 md:mb-6 gap-2">
              <h3 className="text-lg md:text-xl font-semibold font-sans text-foreground">Revenue Forecast</h3>
              <span className="text-xs md:text-sm text-foreground-muted">
                {revenueForecast.current_month} • {revenueForecast.collection_cycle_months || 6}-month collection cycle
              </span>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-3 md:p-4">
                <p className="text-xs md:text-sm text-blue-600 font-medium mb-1">Collection Rate</p>
                <p className="text-lg md:text-2xl font-bold font-mono text-blue-800">{revenueForecast.collection_rate}%</p>
              </div>
              <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-3 md:p-4">
                <p className="text-xs md:text-sm text-amber-600 font-medium mb-1">Total Outstanding</p>
                <p className="text-lg md:text-2xl font-bold font-mono text-amber-800">
                  ${revenueForecast.total_outstanding?.toLocaleString('en-US', { minimumFractionDigits: 0 })}
                </p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-3 md:p-4">
                <p className="text-xs md:text-sm text-purple-600 font-medium mb-1">Pipeline Value</p>
                <p className="text-lg md:text-2xl font-bold font-mono text-purple-800">
                  ${revenueForecast.pipeline_value?.toLocaleString('en-US', { minimumFractionDigits: 0 })}
                </p>
              </div>
              <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg p-3 md:p-4">
                <p className="text-xs md:text-sm text-teal-600 font-medium mb-1">6-Month Forecast</p>
                <p className="text-lg md:text-2xl font-bold font-mono text-teal-800">
                  ${revenueForecast.six_month_forecast?.toLocaleString('en-US', { minimumFractionDigits: 0 })}
                </p>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-3 md:p-4 col-span-2 lg:col-span-1">
                <p className="text-xs md:text-sm text-green-600 font-medium mb-1">Monthly Avg</p>
                <p className="text-lg md:text-2xl font-bold font-mono text-green-800">
                  ${revenueForecast.monthly_avg_collection?.toLocaleString('en-US', { minimumFractionDigits: 0 })}
                </p>
              </div>
            </div>
            <div className="mt-4 md:mt-6 pt-3 md:pt-4 border-t border-border">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 text-xs md:text-sm">
                <div>
                  <span className="text-foreground-muted">Open Claims:</span>
                  <span className="ml-2 font-semibold">{revenueForecast.summary.open_claims}</span>
                </div>
                <div>
                  <span className="text-foreground-muted">Settled Claims:</span>
                  <span className="ml-2 font-semibold">{revenueForecast.summary.settled_claims}</span>
                </div>
                <div>
                  <span className="text-foreground-muted">Paid in Full:</span>
                  <span className="ml-2 font-semibold text-green-600">{revenueForecast.summary.paid_in_full_claims}</span>
                </div>
                <div>
                  <span className="text-foreground-muted">Expected Collection:</span>
                  <span className="ml-2 font-semibold text-blue-600">
                    ${revenueForecast.expected_collection?.toLocaleString('en-US', { minimumFractionDigits: 0 })}
                  </span>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Monthly Settlement Chart */}
        {monthlySettlement.length > 0 && (
          <Card className="p-6 border border-border shadow-sm mb-8" data-testid="monthly-settlement-chart">
            <h3 className="text-xl font-semibold font-sans text-foreground mb-6">Monthly Settlement & Fees</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthlySettlement}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={CHART_TICK_STYLE} />
                <YAxis tick={CHART_TICK_STYLE} tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} />
                <Tooltip 
                  formatter={(value) => `$${value.toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
                  labelStyle={CHART_LABEL_STYLE}
                />
                <Legend />
                <Bar dataKey="total_fees" name="Total Fees" fill="#2563EB" />
                <Bar dataKey="fees_collected" name="Fees Collected" fill="#10B981" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* Claims by Adjuster */}
        {claimsByAdjuster.length > 0 && (
          <Card className="p-6 border border-border shadow-sm mb-8" data-testid="claims-by-adjuster">
            <h3 className="text-xl font-semibold font-sans text-foreground mb-6">Claims by Adjuster</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 font-medium uppercase tracking-wider text-xs">
                  <tr>
                    <th className="px-4 py-3 text-left">Adjuster</th>
                    <th className="px-4 py-3 text-center">Total Claims</th>
                    <th className="px-4 py-3 text-center">Open</th>
                    <th className="px-4 py-3 text-right">Settlement Value</th>
                    <th className="px-4 py-3 text-right">Fees Collected</th>
                    <th className="px-4 py-3 text-right">Outstanding</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {claimsByAdjuster.map((adjuster) => (
                    <tr key={adjuster.adjuster} className="hover:bg-slate-50/50">
                      <td className="px-4 py-3 font-medium text-foreground">{adjuster.adjuster}</td>
                      <td className="px-4 py-3 text-center">{adjuster.total_claims}</td>
                      <td className="px-4 py-3 text-center">
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                          {adjuster.open_claims}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs">
                        ${adjuster.total_settlement_value.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs text-success-text">
                        ${adjuster.fees_collected.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs text-destructive">
                        ${adjuster.fees_outstanding.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Recent Activity */}
        <Card className="p-6 border border-border shadow-sm" data-testid="recent-activity">
          <h3 className="text-xl font-semibold font-sans text-foreground mb-6">Recent Activity</h3>
          <div className="space-y-3">
            {recentActivity.length > 0 ? (
              recentActivity.map((activity) => (
                <div key={`${activity.entity_type}-${activity.entity_id}-${activity.timestamp}`} className="flex items-center justify-between py-3 border-b border-border last:border-0">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">
                      {activity.action} {activity.entity_type}
                    </p>
                    <p className="text-xs text-foreground-muted">
                      {activity.entity_id} • {new Date(activity.timestamp).toLocaleString()}
                    </p>
                  </div>
                  <span className="text-xs px-2 py-1 rounded bg-secondary text-secondary-foreground">
                    {activity.action}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-center text-foreground-muted py-8">No recent activity</p>
            )}
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
};